import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Phone, Calendar, Clock, Copy, CheckCircle2, Mail, User as UserIcon } from 'lucide-react';
import { DatePicker, TimePicker } from '../components/DateTimePicker';
import { localizeOutcome } from '../lib/outcomes';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Canonical Italian values (stored in DB) with their translation keys.
const CALL_OUTCOMES = [
  { value: 'Primo Deposito Effettuato', tKey: 'outcome_first_deposit' },
  { value: 'Appuntamento', tKey: 'outcome_appointment' },
  { value: 'Richiamo', tKey: 'outcome_callback' },
  { value: 'Non Interessato', tKey: 'outcome_not_interested' },
  { value: 'Non Risponde', tKey: 'outcome_no_answer' },
  { value: 'Segreteria', tKey: 'outcome_voicemail' },
  { value: 'Numero Errato', tKey: 'outcome_wrong_number' },
  { value: 'Numero Inesistente', tKey: 'outcome_non_existent' },
  { value: 'Occupato', tKey: 'outcome_busy' },
  { value: 'Cliente già registrato', tKey: 'outcome_already_registered' },
  { value: 'Cliente indeciso', tKey: 'outcome_undecided' },
  { value: 'Cliente richiede informazioni', tKey: 'outcome_needs_info' },
  { value: 'Da ricontattare', tKey: 'outcome_to_recontact' },
  { value: 'Chiamata interrotta', tKey: 'outcome_call_interrupted' },
  { value: 'Cliente non idoneo', tKey: 'outcome_not_eligible' },
];

const copyToClipboard = (text) => {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  } else {
    fallbackCopy(text);
  }
};

const fallbackCopy = (text) => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  document.body.appendChild(textArea);
  textArea.select();
  try {
    document.execCommand('copy');
  } catch (err) {
    console.log('Copy failed', err);
  }
  document.body.removeChild(textArea);
};

const LeadDetail = ({ user, onLogout, theme, onToggleTheme }) => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const [lead, setLead] = useState(null);
  const [calls, setCalls] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [phoneCopied, setPhoneCopied] = useState(false);

  // Call registration state (inline, no modal)
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const [savingCall, setSavingCall] = useState(false);

  // Appointment/Callback modals
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [appointmentNote, setAppointmentNote] = useState('');
  const [callbackDate, setCallbackDate] = useState('');
  const [callbackTime, setCallbackTime] = useState('');
  const [callbackReason, setCallbackReason] = useState('');

  const isAdmin = user.role === 'admin';
  const primary = '#0F62FE';
  const success = '#198038';
  const warning = '#F1C21B';
  const danger = '#FA4D56';

  useEffect(() => {
    fetchLeadDetails();
  }, [leadId]);

  const fetchLeadDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const [leadRes, callsRes] = await Promise.all([
        axios.get(`${API}/leads/${leadId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/calls/lead/${leadId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setLead(leadRes.data);
      setCalls(callsRes.data);

      // Load appointments/callbacks for this lead
      const [apptRes, cbRes] = await Promise.all([
        axios.get(`${API}/appointments`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/callbacks`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setAppointments(apptRes.data.filter((a) => a.lead_id === leadId));
      setCallbacks(cbRes.data.filter((c) => c.lead_id === leadId));
    } catch (error) {
      toast.error(t('ld_load_error'));
      console.error('Error fetching lead details:', error);
    }
  };

  const handleCopyPhone = () => {
    if (!lead) return;
    copyToClipboard(lead.phone);
    setPhoneCopied(true);
    toast.success(t('ld_number_copied').replace('$phone', lead.phone));
    setTimeout(() => setPhoneCopied(false), 2500);
  };

  const handleSaveCall = async () => {
    if (!outcome) {
      toast.error(t('ld_select_outcome'));
      return;
    }
    if (!note.trim()) {
      toast.error(t('ld_note_required'));
      return;
    }

    setSavingCall(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/calls`,
        {
          lead_id: leadId,
          outcome,
          note,
          duration: 0,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success(t('ld_call_registered'));

      if (outcome === 'Appuntamento') {
        setShowAppointmentModal(true);
      } else if (outcome === 'Richiamo') {
        setShowCallbackModal(true);
      }

      setOutcome('');
      setNote('');
      fetchLeadDetails();
    } catch (error) {
      toast.error(t('ld_call_error'));
      console.error('Error saving call:', error);
    } finally {
      setSavingCall(false);
    }
  };

  const handleCreateAppointment = async () => {
    if (!appointmentDate || !appointmentTime) {
      toast.error(t('ld_datetime_required'));
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const appointmentDateTime = new Date(`${appointmentDate}T${appointmentTime}`);
      await axios.post(
        `${API}/appointments`,
        {
          lead_id: leadId,
          appointment_date: appointmentDateTime.toISOString(),
          note: appointmentNote,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(t('ld_appt_created'));
      setShowAppointmentModal(false);
      setAppointmentDate('');
      setAppointmentTime('');
      setAppointmentNote('');
      fetchLeadDetails();
    } catch (error) {
      toast.error(t('ld_appt_error'));
    }
  };

  const handleCreateCallback = async () => {
    if (!callbackDate || !callbackTime) {
      toast.error(t('ld_datetime_required'));
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const callbackDateTime = new Date(`${callbackDate}T${callbackTime}`);
      await axios.post(
        `${API}/callbacks`,
        {
          lead_id: leadId,
          callback_date: callbackDateTime.toISOString(),
          reason: callbackReason,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(t('ld_cb_created'));
      setShowCallbackModal(false);
      setCallbackDate('');
      setCallbackTime('');
      setCallbackReason('');
      fetchLeadDetails();
    } catch (error) {
      toast.error(t('ld_cb_error'));
    }
  };

  if (!lead) {
    return (
      <div className="flex h-screen overflow-hidden">
        <Sidebar user={user} onLogout={onLogout} theme={theme} onToggleTheme={onToggleTheme} collapsed={collapsed} />
        <div className="flex-1 flex items-center justify-center" style={{ backgroundColor: theme === 'dark' ? '#0A0A0A' : '#F8F9FA' }}>
          <p style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>Caricamento...</p>
        </div>
      </div>
    );
  }

  const cardStyle = {
    backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF',
    borderColor: theme === 'dark' ? '#262626' : '#E0E0E0',
  };
  const textPrimary = theme === 'dark' ? '#F4F4F4' : '#161616';
  const textSecondary = theme === 'dark' ? '#A8A8A8' : '#525252';
  const bgSubtle = theme === 'dark' ? '#0A0A0A' : '#F8F9FA';

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar user={user} onLogout={onLogout} theme={theme} onToggleTheme={onToggleTheme} collapsed={collapsed} />
      <div className="flex-1 overflow-y-auto" style={{ backgroundColor: bgSubtle }}>
        <div className="p-6 lg:p-8 max-w-6xl">
          <Button
            variant="ghost"
            onClick={() => navigate('/leads')}
            className="mb-6"
            data-testid="back-to-leads-button"
          >
            <ArrowLeft size={20} className="mr-2" />
            {t('ld_back')}
          </Button>

          {/* Contact Card - Hero */}
          <div
            className="p-6 rounded-lg border mb-6"
            style={cardStyle}
            data-testid="lead-info-card"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 mb-2">
                  <UserIcon size={20} style={{ color: primary }} />
                  <span className="text-xs uppercase tracking-wider" style={{ color: textSecondary }}>
                    {lead.campaign_name}
                  </span>
                </div>
                <h1 className="text-3xl lg:text-4xl font-bold mb-3" style={{ color: textPrimary }}>
                  {lead.first_name} {lead.last_name}
                </h1>
                {lead.email && (
                  <div className="flex items-center gap-2 text-sm mb-1" style={{ color: textSecondary }}>
                    <Mail size={14} />
                    <span>{lead.email}</span>
                  </div>
                )}
                {(lead.country || lead.language) && (
                  <div className="text-sm" style={{ color: textSecondary }}>
                    {[lead.country, lead.language].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>

              {/* Phone + Copy Button - Big & Clear */}
              <div
                className="flex flex-col items-center justify-center p-4 rounded-lg"
                style={{ backgroundColor: bgSubtle, border: `2px solid ${primary}20` }}
              >
                <div className="text-xs uppercase tracking-wider mb-1" style={{ color: textSecondary }}>
                  {t('ld_number_to_call')}
                </div>
                <div
                  className="font-mono font-bold text-xl mb-3 select-all"
                  style={{ color: primary }}
                  data-testid="lead-phone-display"
                >
                  {lead.phone}
                </div>
                <Button
                  className="w-full"
                  style={{ backgroundColor: phoneCopied ? success : primary, color: 'white' }}
                  onClick={handleCopyPhone}
                  data-testid="copy-phone-button"
                >
                  {phoneCopied ? (
                    <>
                      <CheckCircle2 size={18} className="mr-2" />
                      {t('ld_copied')}
                    </>
                  ) : (
                    <>
                      <Copy size={18} className="mr-2" />
                      {t('ld_copy_number')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Register Call Outcome - Always Visible */}
          <div className="p-6 rounded-lg border mb-6" style={cardStyle} data-testid="register-call-card">
            <div className="flex items-center gap-2 mb-4">
              <Phone size={20} style={{ color: primary }} />
              <h2 className="text-xl font-bold" style={{ color: textPrimary }}>
                {t('ld_register_call')}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label htmlFor="outcome-select">
                  {t('ld_outcome_label')}
                </Label>
                <Select value={outcome} onValueChange={setOutcome}>
                  <SelectTrigger className="mt-1" data-testid="outcome-select">
                    <SelectValue placeholder={t('ld_outcome_placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {CALL_OUTCOMES.map((out) => (
                      <SelectItem key={out.value} value={out.value}>
                        {t(out.tKey) || out.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="note-textarea">
                  {t('ld_note_label')}
                </Label>
                <Textarea
                  id="note-textarea"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t('ld_note_placeholder')}
                  rows={3}
                  className="mt-1"
                  data-testid="note-textarea"
                />
              </div>
            </div>

            <Button
              className="w-full md:w-auto btn-primary"
              onClick={handleSaveCall}
              disabled={savingCall}
              data-testid="save-call-button"
            >
              <CheckCircle2 size={18} className="mr-2" />
              {savingCall ? t('ld_saving') : t('ld_save_call')}
            </Button>
            <p className="text-xs mt-2" style={{ color: textSecondary }}>
              {t('ld_outcome_hint')}
            </p>
          </div>

          {/* Quick Actions - Appointments & Callbacks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="p-6 rounded-lg border" style={cardStyle}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Calendar size={20} style={{ color: success }} />
                  <h3 className="text-lg font-bold" style={{ color: textPrimary }}>
                    {t('ld_appointments')} ({appointments.length})
                  </h3>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowAppointmentModal(true)}
                  data-testid="create-appointment-button"
                >
                  + {t('ld_new')}
                </Button>
              </div>
              {appointments.length === 0 ? (
                <p className="text-sm" style={{ color: textSecondary }}>
                  {t('ld_no_appointments')}
                </p>
              ) : (
                <div className="space-y-2">
                  {appointments.slice(0, 3).map((a) => (
                    <div
                      key={a.id}
                      className="p-2 rounded text-sm"
                      style={{ backgroundColor: bgSubtle, borderLeft: `3px solid ${success}` }}
                      data-testid={`appointment-${a.id}`}
                    >
                      <div className="font-medium" style={{ color: textPrimary }}>
                        {new Date(a.appointment_date).toLocaleString(language === 'it' ? 'it-IT' : 'en-GB', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </div>
                      {a.note && <div className="text-xs" style={{ color: textSecondary }}>{a.note}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 rounded-lg border" style={cardStyle}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Clock size={20} style={{ color: warning }} />
                  <h3 className="text-lg font-bold" style={{ color: textPrimary }}>
                    {t('ld_callbacks')} ({callbacks.length})
                  </h3>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowCallbackModal(true)}
                  data-testid="create-callback-button"
                >
                  + {t('ld_new')}
                </Button>
              </div>
              {callbacks.length === 0 ? (
                <p className="text-sm" style={{ color: textSecondary }}>
                  {t('ld_no_callbacks')}
                </p>
              ) : (
                <div className="space-y-2">
                  {callbacks.slice(0, 3).map((c) => (
                    <div
                      key={c.id}
                      className="p-2 rounded text-sm"
                      style={{ backgroundColor: bgSubtle, borderLeft: `3px solid ${warning}` }}
                      data-testid={`callback-${c.id}`}
                    >
                      <div className="font-medium" style={{ color: textPrimary }}>
                        {new Date(c.callback_date).toLocaleString(language === 'it' ? 'it-IT' : 'en-GB', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </div>
                      {c.reason && <div className="text-xs" style={{ color: textSecondary }}>{c.reason}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Facebook Notes - if any */}
          {lead.facebook_notes && (
            <div className="p-6 rounded-lg border mb-6" style={cardStyle}>
              <h3 className="text-lg font-bold mb-2" style={{ color: textPrimary }}>
                {t('ld_fb_notes')}
              </h3>
              <p className="text-sm whitespace-pre-wrap" style={{ color: textSecondary }}>
                {lead.facebook_notes}
              </p>
            </div>
          )}

          {/* Call History - Compact */}
          <div className="p-6 rounded-lg border" style={cardStyle}>
            <h3 className="text-lg font-bold mb-4" style={{ color: textPrimary }} data-testid="call-history-title">
              {t('ld_call_history')} ({calls.length})
            </h3>
            {calls.length === 0 ? (
              <p className="text-sm" style={{ color: textSecondary }}>
                {t('ld_no_calls')}
              </p>
            ) : (
              <div className="space-y-3">
                {calls.map((call) => (
                  <div
                    key={call.id}
                    className="p-3 rounded border"
                    style={{ backgroundColor: bgSubtle, borderColor: theme === 'dark' ? '#262626' : '#E0E0E0' }}
                    data-testid={`call-record-${call.id}`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <span
                        className="px-2 py-1 rounded text-xs font-medium"
                        style={{ backgroundColor: `${primary}20`, color: primary }}
                      >
                        {localizeOutcome(t, call.outcome)}
                      </span>
                      <span className="text-xs font-mono" style={{ color: textSecondary }}>
                        {new Date(call.created_at).toLocaleString(language === 'it' ? 'it-IT' : 'en-GB')}
                      </span>
                    </div>
                    <p className="text-sm mt-1" style={{ color: textPrimary }}>{call.note}</p>
                    <p className="text-xs mt-1" style={{ color: textSecondary }}>
                      {t('operator')}: {call.user_name}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Appointment Modal */}
      <Dialog open={showAppointmentModal} onOpenChange={setShowAppointmentModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('ld_new_appointment')}</DialogTitle>
            <DialogDescription>
              {lead.first_name} {lead.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('date')}</Label>
                <div className="mt-1">
                  <DatePicker
                    value={appointmentDate}
                    onChange={setAppointmentDate}
                    language={language}
                    dataTestId="appointment-date-input"
                  />
                </div>
              </div>
              <div>
                <Label>{t('time')}</Label>
                <div className="mt-1">
                  <TimePicker
                    value={appointmentTime}
                    onChange={setAppointmentTime}
                    language={language}
                    dataTestId="appointment-time-input"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label>{t('note')}</Label>
              <Textarea
                value={appointmentNote}
                onChange={(e) => setAppointmentNote(e.target.value)}
                className="mt-1"
                data-testid="appointment-note-textarea"
              />
            </div>
            <Button className="w-full btn-primary" onClick={handleCreateAppointment} data-testid="appointment-submit-button">
              {t('ld_create_appointment')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Callback Modal */}
      <Dialog open={showCallbackModal} onOpenChange={setShowCallbackModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('ld_new_callback')}</DialogTitle>
            <DialogDescription>
              {lead.first_name} {lead.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('date')}</Label>
                <div className="mt-1">
                  <DatePicker
                    value={callbackDate}
                    onChange={setCallbackDate}
                    language={language}
                    dataTestId="callback-date-input"
                  />
                </div>
              </div>
              <div>
                <Label>{t('time')}</Label>
                <div className="mt-1">
                  <TimePicker
                    value={callbackTime}
                    onChange={setCallbackTime}
                    language={language}
                    dataTestId="callback-time-input"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label>{t('ld_reason')}</Label>
              <Textarea
                value={callbackReason}
                onChange={(e) => setCallbackReason(e.target.value)}
                className="mt-1"
                data-testid="callback-reason-textarea"
              />
            </div>
            <Button className="w-full btn-primary" onClick={handleCreateCallback} data-testid="callback-submit-button">
              {t('ld_schedule_callback')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadDetail;
